import { Seo } from '@components/seo/Seo';
import { ZinePost } from '@components/zinePost/ZinePost';

import { checkPagePreviewAccess } from '@common/utils/access';
import { isDevelopment } from '@common/utils/environment';
import { serverSideWithSiteData } from '@common/utils/page';
import { DEFAULT_IMGIX_PARAMS } from '@data/constants';
import { queryFromDato } from '@data/datocms/client';
import { zineRelatedQuery } from '@data/datocms/queries';
import { zinePostQuery } from '@data/datocms/queries/zinePost';
import {
	DatoZinePostPreview,
	DatoZinePostRecord,
	DatoZinePostResponse,
	DatoZineRelatedResponse,
	SiteData,
} from '@interfaces';

type ZineDetailPageProps = {
	zinePost: DatoZinePostRecord;
	zineRelatedPosts: DatoZinePostPreview[];
};

export default function ZineDetailPage({
	site: { faviconMetaTags },
	zinePost,
	zineRelatedPosts,
}: ZineDetailPageProps & SiteData) {
	return (
		<>
			<Seo metaTagData={[...zinePost.seoMetaTags, ...faviconMetaTags]} />
			<ZinePost post={zinePost} relatedPosts={zineRelatedPosts} />
		</>
	);
}

export const getServerSideProps = serverSideWithSiteData<ZineDetailPageProps>(
	async (context) => {
		const { params, query, draftMode } = context;

		const slug = String(params?.slug);

		const isSharedPreview = await checkPagePreviewAccess(query, slug);

		const includeDrafts = isDevelopment() || draftMode || isSharedPreview;

		const zinePostResponse = await queryFromDato<DatoZinePostResponse>({
			query: zinePostQuery,
			variables: {
				slug,
				imgixParams: DEFAULT_IMGIX_PARAMS,
			},
			includeDrafts,
		});

		const zinePost = zinePostResponse?.zinePost;
		const zineIndex = zinePostResponse?.zineIndex;

		if (!zinePost || !zineIndex) {
			return {
				notFound: true,
			};
		}

		const tags = zinePost.tags.reduce<string[]>((accumulator, { id }) => {
			return [...accumulator, id];
		}, []);

		// related posts
		const relatedCount = 3;

		const zineRelatedResponse = await queryFromDato<DatoZineRelatedResponse>({
			query: zineRelatedQuery,
			variables: {
				first: relatedCount,
				tags,
				imgixParams: {
					...DEFAULT_IMGIX_PARAMS,
					ar: '1',
					fit: 'crop',
				},
				exclude: [zinePost.id],
			},
		});

		const relatedPosts = zineRelatedResponse?.zineRelatedPosts || [];
		const latestPosts = zineRelatedResponse?.zineLatestPosts || [];

		const zineRelatedPosts = [...relatedPosts, ...latestPosts].slice(
			0,
			relatedCount,
		);

		return {
			props: {
				zinePost,
				zineRelatedPosts,
				backgroundColor: zineIndex.backgroundColor,
			},
		};
	},
);
